/**
 * Browser-side Web Push helpers for the Reminders card — isolated here so the
 * card stays declarative and tests can mock this module (jsdom has no
 * PushManager).
 *
 * Platform reality this hides: on iOS, push exists only for Home-Screen-
 * installed PWAs (16.4+) — in plain Safari `PushManager` is absent and
 * `isPushSupported()` is false, which the card turns into the install hint.
 * The permission request MUST run inside a user gesture; `subscribeDevice` is
 * called straight from the button's click handler.
 */

/** The three values the backend stores per device (registerPushSubscription). */
export interface DeviceSubscription {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export type SubscribeResult =
  | { status: 'ok'; subscription: DeviceSubscription }
  | { status: 'denied' }
  | { status: 'unavailable' };

/** True when this browser context can hold a push subscription at all. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Base64url → the BufferSource `pushManager.subscribe` wants the key as. */
function vapidKeyBytes(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return buffer;
}

/** This browser's existing subscription endpoint, or null. Never throws. */
export async function getDeviceSubscriptionEndpoint(): Promise<string | null> {
  if (!isPushSupported()) return null;
  try {
    // getRegistration (not .ready): .ready never resolves where no service
    // worker is registered (dev server), and the card must not hang there.
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    return subscription?.endpoint ?? null;
  } catch {
    return null;
  }
}

/**
 * Ask permission (must be running inside the user's tap) and mint this
 * browser's subscription against its push service. 'unavailable' covers
 * every no-subscription-possible case: unsupported context, no service
 * worker (dev / not installed), or a subscribe failure.
 */
export async function subscribeDevice(vapidPublicKey: string): Promise<SubscribeResult> {
  if (!isPushSupported()) return { status: 'unavailable' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { status: 'denied' };
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return { status: 'unavailable' };
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyBytes(vapidPublicKey),
    });
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      await subscription.unsubscribe().catch(() => {});
      return { status: 'unavailable' };
    }
    return {
      status: 'ok',
      subscription: { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth },
    };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * Drop this browser's subscription. Returns the endpoint that was removed
 * (for the unregister mutation), or null when there was none.
 */
export async function unsubscribeDevice(): Promise<string | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return null;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe().catch(() => {});
    return endpoint;
  } catch {
    return null;
  }
}
